import React, { useEffect } from 'react';
import { Box, Grid, SelectChangeEvent, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import StyledBackButton from '../Common/StyledBackButton';
import StyledButton from '../Common/StyledButton';
import StyledSpan from '../Common/StyledSpan';
import StyledRadioGroup from '../Common/StyledRadioGroup';
import { useAppSelector } from '../../redux/hooks';
import {
  IFolderType,
  closeDeleteActivityModalOnSuccess,
  deleteActivity,
} from '../../redux/activityLibrary/activityLibrarySlice';
import { RootState } from '../../redux/rootReducer';
import { slimScrollbarGreenStyles } from '../../utils/styles';

const StyledGrid = styled(Grid)(() => ({
  background: `linear-gradient(0deg, rgba(124, 174, 190, 0.2), rgba(124, 174, 190, 0.2))`,
  gap: '11px',
}));

interface MoveActivityModalProps {
  handleDismiss: () => void;
  activityId: string;
}

const MoveActivityModal = ({ handleDismiss, activityId }: MoveActivityModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const folders = useAppSelector((state) => state.activityLibrary.userFolders);
  const coachId = useAppSelector((state: RootState) => state.coach.coach?.id);
  const isDeleteActivitySuccess = useAppSelector(
    (state) => state.activityLibrary.isDeleteActivitySuccess
  );
  const deleteActivityLoading = useAppSelector(
    (state) => state.activityLibrary.deleteActivityLoading
  );
  const { id } = useAppSelector((state) => state.activityLibrary.folder);

  const formik = useFormik({
    initialValues: {
      folderId: '',
    },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      folderId: Yup.string().required(t('activityLibrary.validations.moveActivity')),
    }),
    onSubmit: (v) => {
      if (coachId) {
        const payload = {
          id: activityId,
          coachId,
          isMoveToTrash: false,
          folderId: v.folderId !== 'ACTIVITY_LIBRARY' ? v.folderId : null,
          isInsideFolder: !!id,
        };
        dispatch(deleteActivity(payload));
      }
    },
  });

  const filteredFolders = folders
    .filter((folder) => folder.id !== id)
    .map((folder) => ({ value: folder.id, label: folder.name }));

  if (id) {
    filteredFolders.unshift({
      value: 'ACTIVITY_LIBRARY',
      label: t('common.modules.activityLibrary'),
    });
  }

  const handleOnChange = (folderId: SelectChangeEvent<unknown>) => {
    const inputElement = folderId.currentTarget as HTMLInputElement;
    if (inputElement && inputElement.value) {
      formik.setFieldValue('folderId', inputElement.value);
      formik.handleChange('folderId')(inputElement.value);
    }
  };

  useEffect(() => {
    if (isDeleteActivitySuccess) {
      handleDismiss();
      dispatch(closeDeleteActivityModalOnSuccess());
    }
  }, [isDeleteActivitySuccess]);

  return (
    <Grid minHeight={437}>
      <StyledGrid container py={4.5}>
        <Grid item xs={12} textAlign="center">
          <Box component="img" src="/assets/icons/move-to-folder.svg" />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <StyledSpan variant="title2Bold">{t('activityLibrary.move.activity')}</StyledSpan>
        </Grid>
      </StyledGrid>
      <form onSubmit={formik.handleSubmit}>
        <Grid container gap={6} p="52px 32px 36px 32px">
          <Grid width="100%" overflow="auto" maxHeight={120} sx={slimScrollbarGreenStyles}>
            <StyledRadioGroup
              name="folderId"
              options={filteredFolders}
              value={formik.values.folderId}
              onChange={handleOnChange}
              onBlur={formik.handleBlur}
              error={formik.touched.folderId ? formik.errors.folderId : undefined}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="center" gap="20px">
            <StyledBackButton onClick={handleDismiss} fullWidth disabled={deleteActivityLoading}>
              {t('common.dismiss')}
            </StyledBackButton>
            <StyledButton
              loading={deleteActivityLoading}
              fullWidth
              paddingX={48}
              paddingY={16}
              loadingText={t('activityLibrary.filled.moving')}
            >
              {t('activityLibrary.filled.move')}
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default MoveActivityModal;
